import { Controller } from 'react-hook-form';
import {
  InputText,
  InputSize,
  SelectSize,
  Select,
  PermissionEnum,
  IconLoader,
} from '@ui/src';
import { useAppSelector } from '@store';
import InfoRow from '../common/info-row';
import StepPartBlock from '../common/step-part-block';
import { NewApplicationStatus } from '../../../hoc/new-application/types';
import GroupInformation from './parts/group-information';
import SourceOfIncome from './parts/source-of-income/source-of-income';
import SourceAndSizeOfWealth from './parts/source-and-size-of-wealth/source-and-size-of-wealth';
import AffilatedCompanies from './parts/affiliated-companies/affilated-companies';
import GeneralInfo from './parts/general-info';
import Contacts from './parts/contacts';
import ProfessionalInformation from './parts/professional-information';
import Activities from './parts/activities';
import AdditionalInformation from './parts/additional-information';
import useStepOne from './hooks/use-step-one';
import { IndividualApplicationStepOneTypes } from './types';

import commonStyles from '../common/styles.module.css';

const NewApplicationInfoStepOne = ({
  control,
  errors,
  status,
  companyOfficial,
  isEditable,
  clientActivity,
  sourceOfFundsIds,
  hasSumSubId,
  applicationScheme,
  clearErrors,
  questionnarieControl,
  questionnarieClearErrors,
  questionnarieErrors,
  activePermissions,
  regulatedCompanyScheme,
  questionnarieFields,
  sourceOfWealth,
  baseCurrency,
  questionnarieSetValue,
  sourceOfIncome,
  sourceOfWealthForIndividual,
  groupStructurChartFile,
  cofRegulatedCompanyScheme,
  affilatedCompaniesValues,
  setAffilatedCompaniesValues,
  affilatedCompaniesIsLoading,
  affilatedCompaniesIsFetching,
  deleteAffilatedCompanyInProgress,
  onDeleteAffilatedCompany,
  selectedAffilatedCompanyId,
  uboPercentage,
  questionnarieIsLoading,
}: IndividualApplicationStepOneTypes) => {
  const { sourcesOfFunds, clientsActivities, deliveryChannelsIndividual } =
    useAppSelector((state) => state.customersDictionaries);
  const { countriesList: countries } = useAppSelector((state) => state.root);

  const {
    additionalEmails,
    onChangeEmail,
    onDeleteAdditionalEmails,
    setAdditionalEmails,
    employmentActivity,
    otherActivity,
    businessOwnerActivity,
    otherSourceOfFunds,
  } = useStepOne({
    clientsActivities,
    sourcesOfFunds,
    questionnarieSetValue,
    questionnarieFields,
  });

  return (
    <div className={commonStyles.stepBlockWrapper} style={{ zIndex: 20 }}>
      <div className={commonStyles.stepBlockContent}>
        <GeneralInfo
          control={control}
          clearErrors={clearErrors}
          activePermissions={activePermissions}
          status={status}
          errors={errors}
          regulatedCompanyScheme={regulatedCompanyScheme}
          cofRegulatedCompanyScheme={cofRegulatedCompanyScheme}
          questionnarieControl={questionnarieControl}
          questionnarieClearErrors={questionnarieClearErrors}
          questionnarieErrors={questionnarieErrors}
          countries={countries}
          hasSumSubId={hasSumSubId}
          isEditable={isEditable}
          questionnarieFields={questionnarieFields}
          applicationScheme={applicationScheme}
          questionnarieIsLoading={questionnarieIsLoading}
        />
        <Contacts
          control={control}
          clearErrors={clearErrors}
          errors={errors}
          hasSumSubId={hasSumSubId}
          isEditable={isEditable}
          status={status}
          regulatedCompanyScheme={regulatedCompanyScheme}
          questionnarieControl={questionnarieControl}
          questionnarieClearErrors={questionnarieClearErrors}
          questionnarieErrors={questionnarieErrors}
          activePermissions={activePermissions}
          additionalEmails={additionalEmails}
          onChangeEmail={onChangeEmail}
          onDeleteAdditionalEmails={onDeleteAdditionalEmails}
          setAdditionalEmails={setAdditionalEmails}
          questionnarieIsLoading={questionnarieIsLoading}
        />
        {applicationScheme &&
          (applicationScheme?.civilActive ||
            applicationScheme?.criminalOfAnyActive ||
            applicationScheme?.lastThreeYearsAddressHistoryActive ||
            applicationScheme?.nationalInsuranceNumberOfDirectorsActive ||
            applicationScheme?.regulatoryActive ||
            applicationScheme?.businessAndEmploymentActive) && (
            <AdditionalInformation
              applicationScheme={applicationScheme}
              questionnarieControl={questionnarieControl}
              questionnarieClearErrors={questionnarieClearErrors}
              questionnarieErrors={questionnarieErrors}
              activePermissions={activePermissions}
              status={status}
              questionnarieIsLoading={questionnarieIsLoading}
            />
          )}
        {(regulatedCompanyScheme || cofRegulatedCompanyScheme) && (
          <>
            <ProfessionalInformation
              control={control}
              clearErrors={clearErrors}
              errors={errors}
              activePermissions={activePermissions}
              status={status}
              questionnarieControl={questionnarieControl}
              questionnarieClearErrors={questionnarieClearErrors}
              questionnarieErrors={questionnarieErrors}
              countries={countries}
              clientsActivities={clientsActivities}
              employmentActivity={employmentActivity}
              clientActivity={clientActivity}
              otherActivity={otherActivity}
              businessOwnerActivity={businessOwnerActivity}
              questionnarieIsLoading={questionnarieIsLoading}
            />
            {cofRegulatedCompanyScheme &&
              setAffilatedCompaniesValues &&
              onDeleteAffilatedCompany && (
                <AffilatedCompanies
                  questionnarieControl={questionnarieControl}
                  questionnarieClearErrors={questionnarieClearErrors}
                  questionnarieErrors={questionnarieErrors}
                  status={status}
                  isEditable={activePermissions?.includes(
                    PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                  )}
                  questionnarieFields={questionnarieFields}
                  affilatedCompaniesValues={affilatedCompaniesValues}
                  setAffilatedCompaniesValues={setAffilatedCompaniesValues}
                  affilatedCompaniesIsLoading={affilatedCompaniesIsLoading}
                  affilatedCompaniesIsFetching={affilatedCompaniesIsFetching}
                  deleteAffilatedCompanyInProgress={
                    deleteAffilatedCompanyInProgress
                  }
                  onDeleteAffilatedCompany={onDeleteAffilatedCompany}
                  selectedAffilatedCompanyId={selectedAffilatedCompanyId}
                  uboPercentage={uboPercentage}
                  countries={countries}
                  questionnarieIsLoading={questionnarieIsLoading}
                />
              )}
            <GroupInformation
              questionnarieControl={questionnarieControl}
              questionnarieClearErrors={questionnarieClearErrors}
              questionnarieErrors={questionnarieErrors}
              status={status}
              isEditable={activePermissions?.includes(
                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
              )}
              countries={countries}
              sourceOfWealth={sourceOfWealth}
              baseCurrency={baseCurrency}
              questionnarieSetValue={questionnarieSetValue}
              questionnarieFields={questionnarieFields}
              groupStructurChartFile={groupStructurChartFile}
              isCof={companyOfficial}
              questionnarieIsLoading={questionnarieIsLoading}
            />
            <SourceOfIncome
              questionnarieControl={questionnarieControl}
              questionnarieClearErrors={questionnarieClearErrors}
              questionnarieErrors={questionnarieErrors}
              status={status}
              isEditable={activePermissions?.includes(
                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
              )}
              sourceOfIncome={sourceOfIncome}
              questionnarieSetValue={questionnarieSetValue}
              questionnarieFields={questionnarieFields}
              baseCurrency={baseCurrency}
              questionnarieIsLoading={questionnarieIsLoading}
            />
            <SourceAndSizeOfWealth
              questionnarieControl={questionnarieControl}
              questionnarieClearErrors={questionnarieClearErrors}
              questionnarieErrors={questionnarieErrors}
              status={status}
              isEditable={activePermissions?.includes(
                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
              )}
              sourceOfWealthForIndividual={sourceOfWealthForIndividual}
              questionnarieSetValue={questionnarieSetValue}
              questionnarieFields={questionnarieFields}
              baseCurrency={baseCurrency}
              questionnarieIsLoading={questionnarieIsLoading}
            />
            <StepPartBlock title="Net worth" zIndex={23}>
              {questionnarieIsLoading && (
                <div className={commonStyles.dictionaryLoader}>
                  <IconLoader />
                </div>
              )}
              {!questionnarieIsLoading && (
                <>
                  <InfoRow
                    title="Require"
                    id="requiresNetWorth"
                    style={{
                      position: 'relative',
                      zIndex: 17,
                    }}
                    lessPadding
                  >
                    <Controller
                      control={questionnarieControl}
                      name="requiresNetWorth"
                      defaultValue=""
                      render={({ field: { onChange, value, name } }) => (
                        <Select
                          name={name}
                          placeholder="Select an option..."
                          list={[
                            {
                              name: 'Yes',
                              value: 'yes',
                            },
                            {
                              name: 'No',
                              value: 'no',
                            },
                          ]}
                          value={value as string}
                          size={SelectSize.SMALL}
                          onChange={(e) => {
                            onChange(e);
                            questionnarieClearErrors(['requiresNetWorth']);

                            if (e.target.value === 'no') {
                              questionnarieClearErrors(['netWorthAmount']);
                              questionnarieSetValue('netWorthAmount', '');
                            }
                          }}
                          selectWrapperClassName={
                            !activePermissions?.includes(
                              PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                            ) ||
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED
                              ? commonStyles.selectDisabled
                              : ''
                          }
                          disabled={
                            !activePermissions?.includes(
                              PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                            ) ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                          }
                          tabIndex={
                            !activePermissions?.includes(
                              PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                            ) &&
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                              ? -1
                              : 0
                          }
                          error={questionnarieErrors?.requiresNetWorth?.message}
                          fullWidth
                          borderless
                        />
                      )}
                    />
                  </InfoRow>
                  {questionnarieFields?.requiresNetWorth === 'yes' && (
                    <InfoRow
                      title="Net Worth"
                      id="netWorthAmount"
                      style={{
                        position: 'relative',
                        zIndex: 16,
                      }}
                      lessPadding
                    >
                      <Controller
                        control={questionnarieControl}
                        name="netWorthAmount"
                        defaultValue=""
                        render={({ field: { onChange, value, name } }) => (
                          <InputText
                            name={name}
                            placeholder="Enter amount..."
                            value={value as string}
                            size={InputSize.SMALL}
                            onChange={(e) => {
                              onChange(e);
                              questionnarieClearErrors(['netWorthAmount']);
                            }}
                            className="borderless"
                            disabled={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                            tabIndex={
                              !activePermissions?.includes(
                                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                              ) &&
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                                ? -1
                                : 0
                            }
                            error={questionnarieErrors?.netWorthAmount?.message}
                            currency={baseCurrency}
                            amountInput
                          />
                        )}
                      />
                    </InfoRow>
                  )}
                </>
              )}
            </StepPartBlock>
          </>
        )}
        {!companyOfficial && !regulatedCompanyScheme && (
          <Activities
            control={control}
            sourcesOfFunds={sourcesOfFunds}
            activePermissions={activePermissions}
            status={status}
            clearErrors={clearErrors}
            errors={errors}
            sourceOfFundsIds={sourceOfFundsIds}
            otherSourceOfFunds={otherSourceOfFunds}
            clientsActivities={clientsActivities}
            employmentActivity={employmentActivity}
            clientActivity={clientActivity}
            otherActivity={otherActivity}
            countries={countries}
          />
        )}
        {(!applicationScheme || applicationScheme.deliveryChannelVisible) && (
          <StepPartBlock zIndex={22} withoutTitle>
            <InfoRow
              title={
                applicationScheme && applicationScheme?.deliveryChannelTitle
                  ? applicationScheme?.deliveryChannelTitle
                  : 'Delivery channel'
              }
              id="deliveryChannelId"
              lessPadding
            >
              <Controller
                control={control}
                name="deliveryChannelId"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Select
                    name="deliveryChannelId"
                    placeholder="Select an option..."
                    list={deliveryChannelsIndividual}
                    value={value}
                    error={errors?.deliveryChannelId?.message}
                    size={SelectSize.SMALL}
                    onChange={(e) => {
                      onChange(e);
                      clearErrors(['deliveryChannelId']);
                    }}
                    selectWrapperClassName={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED
                        ? commonStyles.selectDisabled
                        : ''
                    }
                    borderless
                    tabIndex={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    disabled={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                    fullWidth
                  />
                )}
              />
            </InfoRow>
          </StepPartBlock>
        )}
      </div>

      <Controller
        control={control}
        name="userId"
        defaultValue=""
        render={({ field: { value } }) => (
          <input type="hidden" name="userId" value={value} tabIndex={-1} />
        )}
      />
    </div>
  );
};

export default NewApplicationInfoStepOne;
